import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Image, 
  Tags, 
  Settings, 
  LogOut,
  Menu,
  X,
  Mail
} from 'lucide-react';
import { Button } from '../ui/button';
import { adminAPI } from '../../services/adminService';
import { useToast } from '../../hooks/use-toast';
import Logo from '../Logo';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!adminAPI.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }

    const storedUser = adminAPI.getStoredUser();
    setUser(storedUser);
  }, [navigate]);

  const handleLogout = async () => {
    await adminAPI.logout();
    toast({ title: 'Logged out successfully' });
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/posts', icon: FileText, label: 'Posts' },
    { path: '/admin/contacts', icon: Mail, label: 'Contacts' },
    { path: '/admin/media', icon: Image, label: 'Media' },
    { path: '/admin/categories', icon: Tags, label: 'Categories' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`bg-gray-900 text-white w-64 min-h-screen p-4 fixed lg:static transition-transform duration-300 ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } z-50`}>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <Logo className="w-10 h-10 brightness-0 invert" />
            <div>
              <h1 className="text-xl font-bold">TYJ Admin</h1>
              <p className="text-xs text-gray-400">Blog Management</p>
            </div>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {user && (
          <div className="mt-auto pt-8">
            <div className="border-t border-gray-700 pt-4">
              <div className="px-4 py-2 mb-2">
                <p className="text-sm font-medium">{user.full_name}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-sm p-4 lg:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2"
          >
            <Menu className="w-6 h-6" />
          </button>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
